import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import { Scenario } from 'types/pricingSimulator';
import { scenarioDetailsParser } from 'parser/pricingSimulator/scenarioDetailsParser';

interface FetchLenseConfigProp extends HookInputProps {
    updateScenarioProperty: <K extends keyof Scenario>(key: K, value: Scenario[K]) => void;
}

function useFetchScenarioData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    updateScenarioProperty,
    extras,
}: FetchLenseConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        refetch,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setError(null);
                updateScenarioProperty('error', null);
                updateScenarioProperty('data', []);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error: any) {
                onFetchError(error?.response?.data?.detail || error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) {
                updateScenarioProperty('error', null);
                setError(null);
            }
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { selectedScenarioInfo = {}, isDropdownAtLowestLevel = true, isViewMode = () => {} } = extras;
            if (payload && Array.isArray(payload?.data)) {
                let mergedData: any[] = [];
                const parsedData = scenarioDetailsParser(
                    payload.data,
                    selectedScenarioInfo,
                    isDropdownAtLowestLevel,
                    isViewMode,
                );
                //not coming from infinite scroll
                if (variables?.offset > 0 && selectedScenarioInfo?.data?.length > 0) {
                    mergedData = [...selectedScenarioInfo?.data, ...parsedData];
                } else {
                    mergedData = parsedData;
                }
                updateScenarioProperty('data', mergedData);
                updateScenarioProperty('count', payload?.total_count ?? 0);
                updateScenarioProperty('offset', variables?.offset);
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        updateScenarioProperty('data', []);
        setLoading(false);
        setError(error);
        updateScenarioProperty('error', error);
    }
    const outputData: outputType = {
        loading,
    };
    return outputData;
}

export { useFetchScenarioData };
